/**
 * The page a share link opens (/s/<code>): no sign-in. A link with a
 * password asks for it first; a revoked, expired or unknown link says so.
 * The report itself is shown read-only, live, with the agency's branding.
 */

import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { ReadOnlyReport } from '../portal/ReadOnlyReport.tsx';
import type { QueryPage } from '../portal/use-read-only-page.ts';
import { publicShareApi, type OpenedShare } from './public-share-api.ts';

function PasswordForm(props: { onUnlock: (password: string) => Promise<void> }): ReactNode {
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await props.onUnlock(password);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That password did not work.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="sl-gate">
      <form className="card sl-card" onSubmit={(event) => void submit(event)}>
        <h1>Protected report</h1>
        <p>Enter the password you were given to view it.</p>
        <label className="rs-field">
          Password
          <input
            type="password"
            autoComplete="off"
            value={password}
            required
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        {problem && (
          <p className="rh-access-msg rh-access-error" role="alert">
            {problem}
          </p>
        )}
        <button type="submit" className="btn" disabled={busy || !password}>
          View report
        </button>
      </form>
    </main>
  );
}

export function SharedLink({ token }: { token: string }): ReactNode {
  const [opened, setOpened] = useState<OpenedShare | null>(null);
  const [pass, setPass] = useState<string | null>(null);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    publicShareApi.open(token, pass).then(setOpened, () => setGone(true));
  }, [token, pass]);

  const query = useCallback<QueryPage>(
    (input) => publicShareApi.query(token, pass, input),
    [token, pass],
  );

  if (gone)
    return (
      <main className="sl-gate">
        <div className="card sl-card" role="alert">
          <h1>This link no longer works</h1>
          <p>
            It may have been turned off or have expired. Ask the person who sent it for a new one.
          </p>
        </div>
      </main>
    );
  if (!opened) return <p className="pt-empty">Loading…</p>;
  if (opened.locked)
    return (
      <PasswordForm
        onUnlock={async (password) => setPass(await publicShareApi.unlock(token, password))}
      />
    );
  return (
    <ReadOnlyReport
      document={{ ...opened.document, key: token }}
      pages={opened.pages}
      clientName={opened.client.name}
      branding={opened.branding}
      catalogue={opened.catalogue}
      query={query}
      canChangeRange={opened.share.allow_range_change}
    />
  );
}
